import { API_BASE_URL, API_KEY } from '../../../api/auth/authApi'
import { ADMIN_TOKEN_KEY } from '../../auth/adminAuth'

export interface VehicleBrand {
  id: number
  name: string
  slug: string
  is_active: boolean
  created_at?: string
}

export interface VehicleBrandPayload {
  name: string
  slug?: string
  is_active?: boolean
}

export interface VehicleBrandApiResponse<T = unknown> {
  success: boolean
  message: string
  data?: T
  warning?: string
}

const API_PATH_URL = `${API_BASE_URL}/api/admin`
const getHeaders = (): HeadersInit => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY)

  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
  }
}

const getErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  const result = await response.json().catch(() => null)

  if (result?.detail) {
    return result.detail
  }

  if (result?.message) {
    return result.message
  }

  if (typeof result === 'object' && result !== null) {
    const firstError = Object.values(result)[0]

    if (Array.isArray(firstError) && firstError.length > 0) {
      return String(firstError[0])
    }

    if (typeof firstError === 'string') {
      return firstError
    }
  }

  return fallback
}

/**
 * GET /api/admin/vehicle-brands/
 */
export async function apiGetVehicleBrands(): Promise<
  VehicleBrandApiResponse<VehicleBrand[]>
> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/vehicle-brands/`,
      {
        method: 'GET',
        headers: getHeaders(),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to load vehicle brands.',
        ),
      }
    }

    const result = await response.json()

    const data = Array.isArray(result)
      ? result
      : result?.results || result?.data || []

    return {
      success: true,
      message: 'Vehicle brands loaded successfully.',
      data,
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

/**
 * POST /api/admin/vehicle-brands/
 */
export async function apiCreateVehicleBrand(
  payload: VehicleBrandPayload,
): Promise<VehicleBrandApiResponse<VehicleBrand>> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/vehicle-brands/`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to create vehicle brand.',
        ),
      }
    }

    const result = await response.json()

    return {
      success: true,
      message: 'Vehicle brand created successfully.',
      data: result?.data || result,
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

/**
 * PUT /api/admin/vehicle-brands/:id/
 */
export async function apiUpdateVehicleBrand(
  id: number,
  payload: VehicleBrandPayload,
): Promise<VehicleBrandApiResponse<VehicleBrand>> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/vehicle-brands/${id}/`,
      {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to update vehicle brand.',
        ),
      }
    }

    const result = await response.json()

    return {
      success: true,
      message: 'Vehicle brand updated successfully.',
      data: result?.data || result,
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

/**
 * DELETE /api/admin/vehicle-brands/:id/
 */
export async function apiDeleteVehicleBrand(
  id: number,
): Promise<VehicleBrandApiResponse> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/vehicle-brands/${id}/`,
      {
        method: 'DELETE',
        headers: getHeaders(),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to delete vehicle brand. There may be vehicles associated with this brand.',
        ),
      }
    }

    return {
      success: true,
      message: 'Vehicle brand deleted successfully.',
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}